/**
 * « Partager ma boutique » — the public address of a shop and the three share links, as PURE functions (no I/O).
 *
 * The public address is `{storefront base}/{locale}/shop/{slug}`. The storefront base is a deployment fact the dashboard
 * cannot guess (it is not the dashboard's own host), so it comes from `VITE_STOREFRONT_BASE_URL` (see `lib/env.ts`).
 * Nothing here ever builds a link from anything the merchant typed other than the slug the API itself validated, and
 * every value placed in a share link is URL-encoded.
 */

export type ShareNetwork = 'whatsapp' | 'facebook' | 'telegram';

/** `http(s)` only, no credentials, no query or fragment, no trailing slash — or `null` when the value cannot be a base. */
export function normalizeStorefrontBaseUrl(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username !== '' || url.password !== '') return null;
  const path = url.pathname.replace(/\/+$/u, '');
  return `${url.origin}${path}`;
}

/** The shop's public address in the language the merchant is using; `null` when the base is unknown or the slug empty. */
export function buildPublicShopUrl(
  baseUrl: string | null,
  locale: string,
  slug: string | null | undefined,
): string | null {
  if (baseUrl === null || slug === null || slug === undefined || slug.trim() === '') return null;
  return `${baseUrl}/${encodeURIComponent(locale)}/shop/${encodeURIComponent(slug)}`;
}

/** The direct « share on … » link: opens the network's own share screen with the message and the address filled in. */
export function buildShareLink(network: ShareNetwork, url: string, message: string): string {
  const encodedUrl = encodeURIComponent(url);
  switch (network) {
    case 'whatsapp':
      return `https://wa.me/?text=${encodeURIComponent(`${message} ${url}`)}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
    case 'telegram':
      return `https://t.me/share/url?url=${encodedUrl}&text=${encodeURIComponent(message)}`;
  }
}
